import {useMemo} from 'react';
import {Flex, Spinner, Text} from '@chakra-ui/react';
import {plannerEventToDatePatientRow} from './plannerBrowserRowAdapters';
import {
  formatPlannerSidebarDateColumn,
  plannerEventDate,
} from './plannerDateTime';
import {PlannerList} from './PlannerSidebarList';
import {searchResultIsSelected} from './plannerSearchResults';
import {plannerSearchHighlightNode} from './PlannerSearchHighlight';
import {
  buildPlannerEncounterDisplay,
  patientHeaderDetailParts,
} from './plannerBrowserDisplay';
import {buildPatientBrowserIdentity} from './PatientBrowserCard';

export {patientHeaderDetailParts};

const patientHeaderAriaLabel = (header: any) =>
  [
    'View',
    header?.name || 'Unknown patient',
    ...patientHeaderDetailParts(header),
  ]
    .filter(Boolean)
    .join(' ');

const searchPatientHeaderCardProps = ({
  header,
  searchQuery,
}: Record<string, any>) => {
  const patient = header?.patient
    ? {
        ...header.patient,
        id: header.patientId || header.patient.id || null,
        localId: header.localPatientId || header.patient.localId || null,
        ehrId: header.ehrPatientId || header.patient.ehrId || null,
        linkedEhrId: header.linkedEhrId || header.patient.linkedEhrId || null,
        language: header.language || header.patient.language || '',
        dialect: header.dialect || header.patient.dialect || '',
        dob: header.dob || header.patient.dob || null,
        mrn: header.mrn || header.patient.mrn || '',
      }
    : null;
  const identity = patient ? buildPatientBrowserIdentity(patient) : null;
  const detailText =
    identity?.metadataText || patientHeaderDetailParts(header).join(' • ');
  const highlightedName = plannerSearchHighlightNode(header?.name, searchQuery);
  const highlightedDetail = plannerSearchHighlightNode(detailText, searchQuery);

  return {
    contextNode: highlightedDetail,
    contextText: detailText,
    identityRow: patient
      ? {
          kind: 'patient',
          patient,
          metadataNode: highlightedDetail,
          nameNode: highlightedName,
        }
      : {
          kind: 'label',
          label: header?.name || 'Unknown patient',
          language: header?.language || '',
          metadataLines: detailText
            ? [{text: detailText, node: highlightedDetail}]
            : [],
          nameNode: highlightedName,
        },
    language: identity?.language || header?.language || '',
    name: identity?.name || header?.name || 'Unknown patient',
    nameNode: highlightedName,
  };
};

const searchResultPatientKey = (result: any) =>
  [
    result?.patientHeader?.localPatientId
      ? `local:${result.patientHeader.localPatientId}`
      : '',
    result?.patientHeader?.ehrPatientId
      ? `ehr:${result.patientHeader.ehrPatientId}`
      : '',
  ]
    .filter(Boolean)
    .join('|') || result?.key;

const buildSearchResultGroupRow = ({
  result,
  onSelectEvent,
  onSelectPatient,
  searchQuery = '',
  selectedEventId = null,
  selectedPatientId = null,
  source = 'search',
  styles,
}: Record<string, any>) => {
  const isSelected = searchResultIsSelected(
    result,
    selectedEventId,
    selectedPatientId,
  );
  const encounterEvents = result?.encounterEvents || [];
  const patientKey = searchResultPatientKey(result);

  const encounterRows = encounterEvents.flatMap((event: any, index: number) => {
    const display = buildPlannerEncounterDisplay(event, {
      index,
      progressiveKey: result.key,
      searchQuery,
      showDateInTimeColumn: true,
    });
    return display.encounterRows;
  });
  if (result.hiddenEncounterCount > 0) {
    encounterRows.push({
      key: `${result.key}-more-encounters`,
      summary: `+${result.hiddenEncounterCount} more`,
      summaryColor: styles.metaTextColor,
      summaryFontSize: 'sm',
      summaryFontWeight: 'normal',
      summaryNoOfLines: 1,
      testId: 'planner-search-more-encounters-row',
    });
  }

  const handleSelect = () => {
    const selectionItem = result?.patientHeader?.selectionItem;
    if (selectionItem) {
      onSelectPatient?.(selectionItem, source);
      return;
    }
    const firstEncounter = encounterEvents[0];
    if (firstEncounter) onSelectEvent?.(firstEncounter);
  };

  return {
    key: result?.key,
    cardKind: 'search',
    ariaLabel: patientHeaderAriaLabel(result?.patientHeader),
    encounterRows,
    identityRowTestId: 'planner-search-combo-patient-row',
    isSelected,
    onSelect: handleSelect,
    rowProps: {
      'data-testid': 'planner-browser-patient-encounter-group',
      'data-search-group': 'true',
      'data-search-group-selected': isSelected ? 'true' : 'false',
      'data-search-group-patient-key': patientKey || undefined,
    },
    ...searchPatientHeaderCardProps({
      header: result.patientHeader,
      searchQuery,
    }),
  };
};

const buildStandaloneSearchEventRow = ({
  result,
  onSelectEvent,
  searchQuery = '',
  selectedEventId = null,
}: Record<string, any>) => {
  const event = result?.event;
  if (!event) return null;
  const isScheduledEvent = String(event?.stage || '').trim() === 'upcoming';
  const baseRow = plannerEventToDatePatientRow(event, {
    cardKind: 'search',
    index: 0,
    onSelect: (selectedEvent: any) => onSelectEvent?.(selectedEvent),
    progressiveKey: result.key,
    searchQuery,
    selectedEventId,
    showTimeColumn: true,
  });
  if (!baseRow) return null;

  const descriptor =
    !event?.patient && event?.source !== 'phoneCall'
      ? String(event?.patientName || '').trim()
      : '';
  const snippet = String(result?.summarySnippet || '').trim();
  const summary =
    snippet ||
    descriptor ||
    String(event?.statusLabel || event?.detailLine || '').trim();
  const badges: Record<string, any>[] = [];
  if (descriptor && descriptor !== summary) {
    badges.push({
      text: descriptor,
      node: plannerSearchHighlightNode(descriptor, searchQuery),
      noOfLines: 1,
    });
  }
  if (snippet && snippet !== summary) {
    badges.push({
      text: snippet,
      node: plannerSearchHighlightNode(snippet, searchQuery),
      noOfLines: 2,
    });
  }

  return {
    ...baseRow,
    key: result.key || baseRow.key,
    animateOnEnter: false,
    cardKind: 'search',
    detailLines: [],
    encounterRows:
      summary && !isScheduledEvent
        ? [
            {
              badges,
              date: formatPlannerSidebarDateColumn(plannerEventDate(event)),
              summary,
              summaryNode: plannerSearchHighlightNode(summary, searchQuery),
              summaryNoOfLines: snippet ? 2 : 1,
              summaryProminent: false,
              time: baseRow.time,
            },
          ]
        : [],
    identityRow: {
      ...(baseRow.identityRow || {}),
      time: undefined,
    },
    rowProps: {
      ...(baseRow.rowProps || {}),
      'data-testid': 'planner-search-event-result',
      'data-search-event-row': 'true',
      'data-search-group-selected': baseRow.isSelected ? 'true' : 'false',
      'data-search-result-type': 'standaloneEventRow',
    },
  };
};

export const PlannerSearchResultList = ({
  error = null,
  loading = false,
  onSelectEvent,
  onSelectPatient,
  results = [],
  selectedEventId = null,
  selectedPatientId = null,
  source = 'search',
  styles,
  searchQuery = '',
  progressiveKey = 'search',
  interactionsDisabled = false,
}: Record<string, any>) => {
  const hasAnyResults = results.length > 0;
  const rows = useMemo(
    () =>
      results
        .map((result: any) =>
          result?.type === 'standaloneEventRow'
            ? buildStandaloneSearchEventRow({
                result,
                onSelectEvent,
                searchQuery,
                selectedEventId,
              })
            : buildSearchResultGroupRow({
                result,
                onSelectEvent,
                onSelectPatient,
                searchQuery,
                selectedEventId,
                selectedPatientId,
                source,
                styles,
              }),
        )
        .filter(Boolean),
    [
      onSelectEvent,
      onSelectPatient,
      searchQuery,
      selectedEventId,
      selectedPatientId,
      source,
      styles,
      results,
    ],
  );

  if (loading && !hasAnyResults) {
    return (
      <Flex
        align="center"
        justify="center"
        py={10}
        aria-live="polite"
        aria-busy="true"
      >
        <Spinner color="opalite.500" size="md" />
      </Flex>
    );
  }

  if (error && !hasAnyResults) {
    return (
      <Text
        color={styles.errorTextColor}
        fontSize="md"
        fontWeight="normal"
        lineHeight="1.4"
        py={3}
      >
        Could not load search results.
      </Text>
    );
  }

  return (
    <PlannerList
      rows={rows}
      dateGrouping="none"
      emptyText=""
      progressive={false}
      animateEntries
      progressiveKey={progressiveKey}
      interactionsDisabled={interactionsDisabled}
      showTimeColumn={false}
      styles={styles}
      testId="planner-search-result-list"
    />
  );
};

export const SearchPatientEncounterStack = PlannerSearchResultList;
